import axios from "@/utils/axios";
import { useQuery } from "@tanstack/react-query";

interface ConfigResponse {
  data: {
    logo?: string;
    institution_name?: string;
    [key: string]: string | undefined;
  };
}

interface AppConfigData {
  logo?: string;
  institution_name?: string;
}

// Hook para obtener todas las configuraciones disponibles
export const useGetAllConfigs = () => {
  return useQuery({
    queryKey: ["config", "all"],
    queryFn: async (): Promise<ConfigResponse> => {
      const response = await axios.get("/v1/config");
      return response.data;
    },
  });
};

// Hook para obtener las keys válidas disponibles
export const useGetValidConfigKeys = () => {
  const { data: allConfigs } = useGetAllConfigs();
  return Object.keys(allConfigs?.data || {});
};

const getConfig = async (keys?: string[]): Promise<ConfigResponse> => {
  const params = keys && keys.length > 0 ? { keys: keys.join(",") } : {};
  const response = await axios.get("/v1/config", { params });
  return response.data;
};

// Hook mejorado que valida las keys antes de hacer la petición
export const useGetConfig = (keys?: string[]) => {
  const { data: allConfigs, isLoading: isLoadingAll } = useGetAllConfigs();
  const validKeys = Object.keys(allConfigs?.data || {});

  // Filtrar solo las keys válidas
  const filteredKeys = keys?.filter((key) => validKeys.includes(key));

  return useQuery({
    queryKey: ["config", filteredKeys],
    queryFn: () => getConfig(filteredKeys),
    enabled: !!allConfigs && !isLoadingAll, // Solo ejecutar cuando tengamos las keys válidas
  });
};

export const useGetConfigByKey = (key: string) => {
  const { data: allConfigs } = useGetAllConfigs();
  const validKeys = Object.keys(allConfigs?.data || {});

  // Solo hacer la petición si la key es válida
  const isValidKey = validKeys.includes(key);

  return useQuery({
    queryKey: ["config", key],
    queryFn: () => getConfig([key]),
    enabled: !!allConfigs && isValidKey, // Solo ejecutar si la key es válida
  });
};

// Catálogo de unidades de tiempo para la caducidad de registros de expediente
export interface ExpirationUnitDto {
  /** Nombre de la unidad en singular, ej. "minute" | "hour" | "day" */
  name: string;
  /** Etiqueta a mostrar, ej. "Minutos" | "Horas" | "Días" */
  label: string;
}

export const useGetExpirationUnits = () => {
  return useQuery({
    queryKey: ["config", "expiration-units"],
    queryFn: async (): Promise<ExpirationUnitDto[]> => {
      const response = await axios.get<{ data: ExpirationUnitDto[] }>(
        "/admin/v1/config/expiration-units",
      );
      return response.data.data;
    },
    staleTime: Infinity, // Catálogo estático
  });
};

// Hook específico para obtener configuración de la aplicación (logo e institution_name)
export const useGetAppConfig = () => {
  return useQuery({
    queryKey: ["config", "app"],
    queryFn: async (): Promise<AppConfigData> => {
      const response = await axios.get("/v1/config", {
        params: { keys: "logo,institution_name" },
      });

      // Intentar diferentes estructuras de respuesta
      const logo = response.data.data?.logo || response.data.logo;
      const institution_name =
        response.data.data?.institution_name || response.data.institution_name;

      return {
        logo,
        institution_name,
      };
    },
  });
};
